import React from 'react';
import { chatScriptService } from '../../services/chat-script.service';
import type { ScriptItem } from '../../components/chatScripts/ScriptList/ScriptList';

export type UseScriptSearchParams = {
  query: string;
  allItems: ScriptItem[];
  creatorId?: string | null;
};

export type UseScriptSearchResult = {
  debounced: string;
  shouldUseBackend: boolean;
  items: ScriptItem[];
  loading: boolean;
  listRef: React.RefObject<HTMLDivElement | null>;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
};

export function useScriptSearch({ query, allItems, creatorId }: UseScriptSearchParams): UseScriptSearchResult {
  const [debounced, setDebounced] = React.useState('');
  const [results, setResults] = React.useState<ScriptItem[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const lastRequestedPageRef = React.useRef<number>(0);
  const isFetchingRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    const h = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(h);
  }, [query]);

  const filtered = React.useMemo(() => {
    const q = debounced.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((it) => (it.title || '').toLowerCase().includes(q));
  }, [debounced, allItems]);

  const shouldUseBackend = debounced.trim().length > 0 && !!creatorId;

  const fetchPage = React.useCallback(
    async (nextPage: number) => {
      if (!creatorId) return;
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);
      try {
        const res = await chatScriptService.searchScripts({ text: debounced.trim(), creatorId, page: nextPage });
        const mapped: ScriptItem[] = (res.data || []).map((s: any) => ({ id: s._id, title: s.title, message: s.message, price: s.price ?? 0, type: 'script' }));
        setResults((prev) => (nextPage === 1 ? mapped : [...prev, ...mapped]));
        setPage(nextPage);
        setTotalPages(res.totalPages || 0);
      } catch (e) {
        console.error('Failed to search scripts', e);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [creatorId, debounced]
  );

  React.useEffect(() => {
    if (!shouldUseBackend) {
      setResults([]);
      setPage(1);
      setTotalPages(0);
      lastRequestedPageRef.current = 0;
      return;
    }
    setResults([]);
    setPage(1);
    setTotalPages(0);
    lastRequestedPageRef.current = 1;
    fetchPage(1);
  }, [shouldUseBackend, fetchPage]);

  React.useEffect(() => {
    if (!shouldUseBackend) return;
    const sentinel = sentinelRef.current;
    const rootEl = listRef.current;
    if (!rootEl) return;
    const MIN_PAGE_SIZE = 15;
    if ((results.length || 0) < MIN_PAGE_SIZE && page === 1 && loading) return;
    const isScrollable = rootEl.scrollHeight > rootEl.clientHeight;
    if (!isScrollable) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        const hasMore = totalPages > 0 ? page < totalPages : (results.length || 0) % 15 === 0;
        if (!first.isIntersecting || !hasMore || loading) return;
        const nextPage = page + 1;
        if (lastRequestedPageRef.current >= nextPage) return;
        lastRequestedPageRef.current = nextPage;
        fetchPage(nextPage);
      },
      { root: rootEl, rootMargin: '0px 0px 400px 0px', threshold: 0 }
    );
    if (sentinel) observer.observe(sentinel);
    return () => observer.disconnect();
  }, [shouldUseBackend, results.length, page, totalPages, loading, fetchPage]);

  const items = shouldUseBackend ? results : filtered;

  return { debounced, shouldUseBackend, items, loading, listRef, sentinelRef };
}
